import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { LogOut, Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { visibleAdminPages } from '../../utils/roles';

const LOGO_DARK = '/images/logo.png';
const LOGO_LIGHT = '/images/logo-dark.png';

const AdminLayout = ({ children, user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, updateTheme } = useTheme();

  // Filter sidebar entries by the signed-in user's role. Keeps the desk-by-desk
  // layout: e.g. a `support` user sees only Dashboard + Support Tickets.
  const navigation = useMemo(
    () =>
      visibleAdminPages(user?.role).map((p) => ({
        name: p.label,
        href: p.href,
        icon: p.icon,
        key: p.key,
      })),
    [user?.role]
  );

  const toggleTheme = () => {
    updateTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass-card border-b border-border sticky top-0 z-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors text-foreground shrink-0"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <Link to="/admin" className="flex items-center gap-2 sm:gap-3 min-w-0">
                <img
                  src={theme === 'dark' ? LOGO_DARK : LOGO_LIGHT}
                  alt="Aqua blockchain"
                  className="h-8 w-auto shrink-0"
                />
                <h1 className="hidden sm:block text-base sm:text-xl font-bold text-foreground">Admin</h1>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="hover:bg-muted text-foreground"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <span className="text-muted-foreground hidden sm:block">{user?.full_name}</span>
              <Button onClick={handleLogout} variant="ghost" className="text-destructive hover:text-destructive/90" data-testid="admin-logout-button">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        <aside
          className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 border-r transition-transform duration-300 ease-in-out`}
          style={{ background: 'var(--admin-sidebar-bg)', borderColor: 'var(--admin-sidebar-border)' }}
        >
          <nav className="p-4 space-y-1 mt-16 lg:mt-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || (item.href !== '/admin' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${isActive
                    ? 'bg-primary text-primary-foreground font-semibold'
                    : 'text-foreground/80 hover:bg-muted'
                    }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full">{children}</div>
        </main>
      </div>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default AdminLayout;
